import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import { Icon } from "./Icons";
import TherapistPortrait from "./TherapistPortrait";
import {
    THERAPISTS,
    SPECIALIZATIONS,
    GENDERS,
    filterTherapists,
} from "./therapists";
import "./Therapy.css";
import "./TherapyPages.css";
import "./Therapists.css";

function TherapyTherapists() {
    const [selectedSpecs, setSelectedSpecs] = useState([]);
    const [selectedGenders, setSelectedGenders] = useState([]);
    const [couplesOnly, setCouplesOnly] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggle = (list, setList, value) => {
        setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    };

    const filtered = useMemo(
        () =>
            filterTherapists({
                specializations: selectedSpecs,
                genders: selectedGenders,
                offersCouples: couplesOnly ? true : null,
            }),
        [selectedSpecs, selectedGenders, couplesOnly]
    );

    const clearFilters = () => {
        setSelectedSpecs([]);
        setSelectedGenders([]);
        setCouplesOnly(false);
    };

    const activeFilterCount =
        selectedSpecs.length + selectedGenders.length + (couplesOnly ? 1 : 0);

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            <section className="page-header">
                <div className="demo-container">
                    <p className="demo-eyebrow">Our therapists</p>
                    <h1>Find the right person.</h1>
                    <p className="page-lede">
                        Eight licensed clinicians, each with their own focus areas and
                        approach. Filter by what you're looking for, or reach out for
                        a free consultation if you're not sure where to start.
                    </p>
                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container therapists-layout">
                    <aside className={`therapist-filters ${filtersOpen ? "filters-open" : ""}`}>
                        <div className="filter-header">
                            <h3>Filters</h3>
                            {activeFilterCount > 0 && (
                                <button className="filter-clear" onClick={clearFilters}>
                                    Clear ({activeFilterCount})
                                </button>
                            )}
                        </div>

                        <div className="filter-group">
                            <h4>Specialization</h4>
                            <div className="filter-options">
                                {SPECIALIZATIONS.map((spec) => (
                                    <label key={spec} className="filter-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedSpecs.includes(spec)}
                                            onChange={() => toggle(selectedSpecs, setSelectedSpecs, spec)}
                                        />
                                        <span className="filter-checkbox-box">
                                            {selectedSpecs.includes(spec) && <Icon name="check" size={12} />}
                                        </span>
                                        <span className="filter-checkbox-label">{spec}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <h4>Gender</h4>
                            <div className="filter-options">
                                {GENDERS.map((gender) => (
                                    <label key={gender} className="filter-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedGenders.includes(gender)}
                                            onChange={() => toggle(selectedGenders, setSelectedGenders, gender)}
                                        />
                                        <span className="filter-checkbox-box">
                                            {selectedGenders.includes(gender) && <Icon name="check" size={12} />}
                                        </span>
                                        <span className="filter-checkbox-label">{gender}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <h4>Other</h4>
                            <div className="filter-options">
                                <label className="filter-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={couplesOnly}
                                        onChange={() => setCouplesOnly(!couplesOnly)}
                                    />
                                    <span className="filter-checkbox-box">
                                        {couplesOnly && <Icon name="check" size={12} />}
                                    </span>
                                    <span className="filter-checkbox-label">Sees couples</span>
                                </label>
                            </div>
                        </div>
                    </aside>

                    <div className="therapists-results">
                        <div className="results-header">
                            <p className="results-count">
                                {filtered.length === THERAPISTS.length
                                    ? `Showing all ${THERAPISTS.length} therapists`
                                    : `${filtered.length} of ${THERAPISTS.length} therapists`}
                            </p>
                            <button
                                className="filters-toggle"
                                onClick={() => setFiltersOpen(!filtersOpen)}
                            >
                                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                            </button>
                        </div>

                        {filtered.length === 0 ? (
                            <div className="therapists-empty">
                                <p>
                                    No therapists match every filter. Try removing one,
                                    or{" "}
                                    <Link to={path("booking")}>
                                        schedule a consultation
                                    </Link>{" "}
                                    and we'll help you find someone.
                                </p>
                            </div>
                        ) : (
                            <div className="therapists-grid">
                                {filtered.map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={path(`therapists/${t.slug}`)}
                                        className="therapist-card"
                                    >
                                        <div className="therapist-card-portrait">
                                            <TherapistPortrait therapist={t} size="card" />
                                        </div>
                                        <div className="therapist-card-body">
                                            <div className="therapist-card-header">
                                                <h3>{t.name}</h3>
                                                <span className="therapist-card-cred">
                                                    {t.credential} · {t.pronouns}
                                                </span>
                                            </div>
                                            <p className="therapist-card-bio">{t.bioShort}</p>
                                            <div className="therapist-card-tags">
                                                {t.specializations.slice(0, 3).map((spec) => (
                                                    <span key={spec} className="therapist-tag">
                                                        {spec}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="therapist-card-footer">
                                                <span className="therapist-card-fee">
                                                    From ${t.fee}/session
                                                </span>
                                                <span className="therapist-card-link">
                                                    View profile →
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="demo-section demo-cta">
                <div className="demo-container">
                    <h2>Not sure who's the right fit?</h2>
                    <p>
                        The free consultation is twenty minutes on the phone. We'll
                        talk through what you're looking for and match you with the
                        clinician who fits best — even if that's not someone on this
                        page.
                    </p>
                    <div className="demo-actions demo-actions-center">
                        <Link to={path("booking")} className="btn-primary">
                            Schedule a consultation
                        </Link>
                    </div>
                </div>
            </section>

            <TherapyFooter />
        </main>
    );
}

export default TherapyTherapists;